import { Typography } from '@mui/material';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Person } from 'sr-types/lib/person/v1/graphql';
import type { ContactInput } from 'sr-types/lib/shortlist/v1/graphql';
import type { EntityName } from '../../common/auth/Security';
import ButtonWithIcon from '../../common/components/ButtonWithIcon';
import ContactFieldInputs from '../../common/components/contacts/ContactFieldInputs';
import ContactRenderer from '../../common/components/contacts/ContactRenderer';
import {
    personWithBusinessContactsQuery,
    useGetPersonContactInfo,
    useUpdateProfessionalExperiences
} from '../../common/components/person/person';
import { CONSTANTS_ID, SIZES } from '../../common/constants/constants';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { FORM_WIDGET_COMPONENTS } from '../../common/form/formConstants';
import { FormContext } from '../../common/form/FormContext';
import FormWidget from '../../common/form/FormWidget';
import InlineFieldsEditor from '../../common/form/widgets/InlineFieldsEditor';
import I18n, { useI18n } from '../../common/i18n/I18n';
import { Icons } from '../../common/icons/Icons';
import { V } from '../../common/Layout';
import type { Reference } from '../../common/reference/reference';
import { getContactNameFromObject } from '../../common/utils/commonUtils';
import {
    getAddedBusinessContactsIds,
    useDirectlyAddBusinessContact
} from '../../person/detailsPage/detailsPageUtilities/personBusinessContacts';
import { useRepresentativeDefaults } from '../hooks/useRepresentativeDefaults';
import { createRepresentativeEditorListeners } from '../shortlist';
import {
    buildPrefilledRepresentative,
    buildRepresentativeExperienceInput,
    fillOrganizationReferenceLabel,
    getItemPersonReference,
    getOrganizationReference,
    getPrimaryContactIndex,
    shouldFetchRepresentativeDefaults,
    type RepresentativeDefaults
} from '../utils/representativeDefaultsUtils';
import { groupRepresentativesByRole } from '../utils/representativeDisplayUtils';

interface RepresentativeFormItemProps {
    /** Save representatives directly to the selected person's business contacts. */
    directAddContact: boolean;
    /** Opens or closes the quick-add person editor. */
    setShowQuickAddPerson?: Dispatch<SetStateAction<boolean>>;
    /** Stores quick-add person draft data when creating a new representative contact. */
    setQuickAddPersonData?: Dispatch<SetStateAction<Map<string, string>>>;
    /** Representative data returned from quick-add person creation. */
    quickAddRepresentativeInitialData?: ContactInput;
    /** Disable automatic fetching of business contacts from itemInfo (for contexts without itemInfo) */
    disableAutoFetch?: boolean;
    /**
     * Form field holding the person whose primary business contact is pulled in as the representative,
     * for contexts where the person is not in `itemInfo` (e.g. the IP deal talent form's `name` field).
     */
    personFieldName?: string;
    /** Disable the representative contact, role, and organization fields. */
    disabledFields?: boolean;
    /** Tooltip shown while the representative fields are disabled. */
    disabledFieldsTooltip?: ReactNode;
}

type IdentifiedRepresentative = ContactInput & {
    representativeActionId: string;
};

type RepresentativeContactRendererProps = {
    representative: IdentifiedRepresentative;
    isActive: boolean;
    setCurrentPerson: Dispatch<SetStateAction<string | null>>;
    onEditRepresentative: (representativeId: string) => void;
    handleDeleteConfirmed: () => void;
    isDeleteConfirmationOpen: boolean;
    setDeleteConfirmationRepresentativeId: Dispatch<SetStateAction<string | null>>;
    entityName: EntityName;
};

const REPRESENTATIVE_ROLE_HEADING_SX = {
    color: 'text.secondary',
    fontSize: '16px',
    lineHeight: 1.25
};

const RepresentativeContactRenderer = React.memo(function RepresentativeContactRenderer({
    representative,
    isActive,
    setCurrentPerson,
    onEditRepresentative,
    handleDeleteConfirmed,
    isDeleteConfirmationOpen,
    setDeleteConfirmationRepresentativeId,
    entityName
}: RepresentativeContactRendererProps) {
    const representativeActionId = representative.representativeActionId;

    const handleEdit = useCallback(() => {
        onEditRepresentative(representativeActionId);
    }, [onEditRepresentative, representativeActionId]);

    const handleDeleteClicked = useCallback(() => {
        setDeleteConfirmationRepresentativeId(representativeActionId);
    }, [representativeActionId, setDeleteConfirmationRepresentativeId]);

    const handleDeleteConfirmationOpenChange = useCallback(
        (open: boolean) => {
            setDeleteConfirmationRepresentativeId(open ? representativeActionId : null);
        },
        [representativeActionId, setDeleteConfirmationRepresentativeId]
    );

    return (
        <ContactRenderer
            contact={representative}
            contactActionId={representativeActionId}
            currentPerson={isActive ? representativeActionId : null}
            setCurrentPerson={setCurrentPerson}
            onEdit={handleEdit}
            onDeleteClicked={handleDeleteClicked}
            isDeleteConfirmationOpen={isDeleteConfirmationOpen}
            handleDeleteConfirmed={handleDeleteConfirmed}
            removingBusinessContact={false}
            setIsDeleteConfirmationOpen={handleDeleteConfirmationOpenChange}
            entityName={entityName}
            hideBottomBorder
        />
    );
});

export default function RepresentativeFormItem({
    directAddContact = false,
    setShowQuickAddPerson,
    setQuickAddPersonData,
    quickAddRepresentativeInitialData,
    disableAutoFetch = false,
    personFieldName,
    disabledFields = false,
    disabledFieldsTooltip
}: RepresentativeFormItemProps) {
    const { state, handleChange } = useContext(FormContext);
    const { getContactInfo, contactInfoLoading } = useGetPersonContactInfo(personWithBusinessContactsQuery);
    const { getRepresentativeFieldsFromSlimSearch } = useRepresentativeDefaults();
    const { updatePersonExperience, isSaving: isProfileOrganizationSaving } = useUpdateProfessionalExperiences();
    const { directlyAddBusinessContact, isBusinessContactsSaving } = useDirectlyAddBusinessContact();
    const t = useI18n();
    const [deleteConfirmationRepresentativeId, setDeleteConfirmationRepresentativeId] = useState<string | null>(null);
    const [editingRepresentativeId, setEditingRepresentativeId] = useState<string | null>(null);
    const representatives: ContactInput[] = useMemo(() => state?.representatives || [], [state?.representatives]);
    const representativeActionIdsRef = useRef<WeakMap<ContactInput, string>>(new WeakMap());
    const identifiedRepresentativesRef = useRef<WeakMap<ContactInput, IdentifiedRepresentative>>(new WeakMap());
    const representativeActionIdCounterRef = useRef(0);
    const getRepresentativeActionId = useCallback((representative?: ContactInput, index?: number): string => {
        if (!representative) {
            return index !== undefined ? `representative-${index}` : '';
        }

        const existingActionId = representativeActionIdsRef.current.get(representative);
        if (existingActionId) {
            return existingActionId;
        }

        const personReferenceId = representative.personReference?.id || 'representative';
        const nextActionId = `${personReferenceId}-${representativeActionIdCounterRef.current}`;
        representativeActionIdCounterRef.current += 1;
        representativeActionIdsRef.current.set(representative, nextActionId);
        return nextActionId;
    }, []);
    const hasRepresentatives = representatives.length > 0;
    const personValue = personFieldName ? state?.[personFieldName] : state?.itemInfo;
    // New line items have no row security yet; use parent Shortlist until the line item exists.
    const representativeEntityName = state?.identity?.id
        ? ENTITY_CONSTANTS.shortlistLineitem
        : ENTITY_CONSTANTS.shortlist;
    const editingRepresentative = useMemo(
        () =>
            editingRepresentativeId
                ? representatives.find(
                      (representative, index) =>
                          getRepresentativeActionId(representative, index) === editingRepresentativeId
                  )
                : undefined,
        [editingRepresentativeId, getRepresentativeActionId, representatives]
    );
    const editingRepresentativePersonId = editingRepresentative?.personReference?.id || '';
    const currentRepresentative = useMemo(
        () =>
            editingRepresentative
                ? {
                      ...editingRepresentative,
                      organizationReference: getOrganizationReference({
                          ...(editingRepresentative.organizationReference || {}),
                          label:
                              editingRepresentative.organizationReference?.label || editingRepresentative.organization,
                          name: editingRepresentative.organization
                      }),
                      personReference: {
                          ...editingRepresentative?.personReference,
                          label:
                              getContactNameFromObject(editingRepresentative?.name) ||
                              editingRepresentative?.personReference?.label
                      }
                  }
                : null,
        [editingRepresentative]
    );
    const [isEditorOpen, setIsEditorOpen] = useState(!hasRepresentatives);
    const [activeRepresentativePersonId, setActiveRepresentativePersonId] = useState<string | null>(null);
    const prevItemReferenceId = useRef<string | null>(null);
    const isInitialMount = useRef<boolean>(true);
    const [disableSaveButton, setDisableSaveButton] = useState(true);
    const [isRepresentativeSaving, setIsRepresentativeSaving] = useState(false);
    const representativeGroups = useMemo(
        () =>
            groupRepresentativesByRole<IdentifiedRepresentative>(
                representatives.map((representative, index) => {
                    const representativeActionId = getRepresentativeActionId(representative, index);
                    const existingRepresentative = identifiedRepresentativesRef.current.get(representative);

                    if (existingRepresentative?.representativeActionId === representativeActionId) {
                        return existingRepresentative;
                    }

                    const identifiedRepresentative = {
                        ...representative,
                        representativeActionId
                    };

                    identifiedRepresentativesRef.current.set(representative, identifiedRepresentative);
                    return identifiedRepresentative;
                }),
                t('shortlist.representative.role.unspecified')
            ),
        [getRepresentativeActionId, representatives, t]
    );

    useEffect(() => {
        // Skip auto-fetch if disabled (e.g., when used in contexts without itemInfo)
        if (disableAutoFetch) {
            return;
        }

        // Line item itemInfo (e.g. after quick-adding a person) keeps the entity type on `ref`, not at the top level
        const personReference = getItemPersonReference(personValue);
        const itemId = personReference?.id;
        const lineItemId = state?.identity?.id;
        const isPersonType = personReference?.type === ENTITY_CONSTANTS.person;

        // Skip the first call if we're opening in edit mode (have existing line item and representatives)
        if (isInitialMount.current) {
            isInitialMount.current = false;

            if (personValue && (personFieldName || lineItemId) && hasRepresentatives) {
                prevItemReferenceId.current = itemId;
                setIsEditorOpen(false);
                return;
            }
        }

        // Reset representatives if itemInfo is undefined
        if (!personValue || (personFieldName && (!itemId || !isPersonType))) {
            handleChange('representatives', []);
            setIsEditorOpen(true);
            setEditingRepresentativeId(null);
            prevItemReferenceId.current = null;
            return;
        }

        // Skip fetching if no itemId, same itemId as before, or not a person type
        if (!itemId || itemId === prevItemReferenceId.current || !isPersonType) {
            return;
        }

        prevItemReferenceId.current = itemId;

        const fetchContactInfo = async () => {
            try {
                const contactInfo: Person = await getContactInfo(itemId);

                if (prevItemReferenceId.current !== itemId) {
                    return;
                }

                const businessContacts = contactInfo?.businessContacts || [];

                if (businessContacts.length > 0) {
                    const primaryContactIndex = getPrimaryContactIndex(businessContacts);

                    const prefilledRepresentatives = await Promise.all(
                        businessContacts.map(async (businessContact, index) => {
                            let defaultRepresentativeFields: RepresentativeDefaults = {};

                            // Defaults are optional enrichment: a failed lookup must not drop the other contacts
                            if (shouldFetchRepresentativeDefaults(businessContact)) {
                                try {
                                    defaultRepresentativeFields = await getRepresentativeFieldsFromSlimSearch(
                                        businessContact.personReference.id
                                    );
                                } catch (error) {
                                    console.warn('Failed to fetch representative defaults:', error);
                                }
                            }

                            return buildPrefilledRepresentative(
                                businessContact,
                                defaultRepresentativeFields,
                                index === primaryContactIndex
                            );
                        })
                    );

                    if (prevItemReferenceId.current !== itemId) {
                        return;
                    }

                    handleChange('representatives', prefilledRepresentatives);

                    setEditingRepresentativeId(null);
                    setDisableSaveButton(true);
                    setIsEditorOpen(false);
                } else {
                    // Clear representatives if no business contacts found
                    handleChange('representatives', []);
                    setIsEditorOpen(true);
                }
            } catch (error) {
                if (prevItemReferenceId.current !== itemId) {
                    return;
                }
                // Silently fail, user can still manually enter representative details
                console.warn('Failed to fetch contact info:', error);
                // Clear representatives on error
                handleChange('representatives', []);
                setIsEditorOpen(true);
            }
        };

        fetchContactInfo();
    }, [
        personValue,
        personFieldName,
        state?.identity?.id,
        hasRepresentatives,
        getContactInfo,
        getRepresentativeFieldsFromSlimSearch,
        handleChange,
        disableAutoFetch
    ]);

    const handleCloseEditor = useCallback(() => {
        setShowQuickAddPerson?.(false);
        setEditingRepresentativeId(null);
        setDisableSaveButton(true);
        if (hasRepresentatives) {
            setIsEditorOpen(false);
        }
    }, [setShowQuickAddPerson, hasRepresentatives]);

    const getDefaultRepresentativeFieldsForPerson = useCallback(
        (personId: string) => getRepresentativeFieldsFromSlimSearch(personId),
        [getRepresentativeFieldsFromSlimSearch]
    );

    const syncRepresentativeExperience = useCallback(
        async (
            contactState: ContactInput,
            organizationReference?: Reference,
            existingRepresentativeFields: RepresentativeDefaults = {}
        ) => {
            if (!contactState?.personReference?.id) {
                return;
            }

            // Profile sync is a cross-entity side effect; only attempt it when Person ABAC grants edit.
            if (!existingRepresentativeFields.canEditProfile) {
                return;
            }

            const input = buildRepresentativeExperienceInput({
                organizationReference,
                role: contactState.role || undefined,
                existingRepresentativeFields
            });

            if (!input) {
                return;
            }

            await updatePersonExperience({
                id: contactState.personReference.id,
                input
            });
        },
        [updatePersonExperience]
    );

    const createRepresentativeWithContactDetails = useCallback(
        async (contactState: ContactInput): Promise<void> => {
            if (directAddContact) {
                await directlyAddBusinessContact(contactState, representatives);
                handleCloseEditor();
                return;
            }
            try {
                const contactDetails: Person = await getContactInfo(contactState.personReference.id);
                const existingRepresentativeFields = await getRepresentativeFieldsFromSlimSearch(
                    contactState.personReference.id
                );
                const selectedOrganizationReference = getOrganizationReference(contactState.organizationReference);
                const organizationReference =
                    fillOrganizationReferenceLabel(
                        selectedOrganizationReference,
                        existingRepresentativeFields.organizationReference
                    ) || existingRepresentativeFields.organizationReference;
                const organizationReferenceWithLabel = organizationReference
                    ? {
                          ...organizationReference,
                          label:
                              organizationReference.label ||
                              selectedOrganizationReference?.label ||
                              contactState.organization
                      }
                    : undefined;
                const role = contactState.role || existingRepresentativeFields.role;
                const representativeInput = { ...contactState, role };

                await syncRepresentativeExperience(
                    representativeInput,
                    organizationReferenceWithLabel,
                    existingRepresentativeFields
                );

                // Save representative with contactState and additional fields from contactDetails
                const representativeToSave = {
                    ...representativeInput,
                    contactInfo: contactDetails?.contactInfo,
                    profileImage: contactDetails?.profileImage,
                    name: contactDetails?.name,
                    organizationReference: organizationReferenceWithLabel,
                    organization: organizationReferenceWithLabel?.label
                };

                const currentEditingRepresentativeIndex = editingRepresentativeId
                    ? representatives.findIndex(
                          (representative, index) =>
                              getRepresentativeActionId(representative, index) === editingRepresentativeId
                      )
                    : -1;
                const nextRepresentatives =
                    currentEditingRepresentativeIndex === -1
                        ? [...representatives, representativeToSave]
                        : representatives.map((representative, index) =>
                              index === currentEditingRepresentativeIndex ? representativeToSave : representative
                          );

                handleChange('representatives', nextRepresentatives);
                setEditingRepresentativeId(null);
                setIsEditorOpen(false);
                setDisableSaveButton(true);
            } catch (error) {
                console.error('Failed to fetch contact info on save:', error);
                setIsEditorOpen(true);
            }
        },
        [
            directAddContact,
            directlyAddBusinessContact,
            representatives,
            editingRepresentativeId,
            getRepresentativeActionId,
            getContactInfo,
            getRepresentativeFieldsFromSlimSearch,
            syncRepresentativeExperience,
            handleChange,
            handleCloseEditor
        ]
    );

    const handleSaveRepresentative = useCallback(
        async (representative: ContactInput) => {
            setIsRepresentativeSaving(true);
            try {
                await createRepresentativeWithContactDetails(representative);
            } finally {
                setIsRepresentativeSaving(false);
            }
        },
        [createRepresentativeWithContactDetails]
    );

    const handleAddRepresentative = useCallback(() => {
        setEditingRepresentativeId(null);
        setDisableSaveButton(true);
        setIsEditorOpen(true);
    }, []);

    const handleEditRepresentative = useCallback((representativeId: string) => {
        setEditingRepresentativeId(representativeId);
        setDisableSaveButton(false);
        setIsEditorOpen(true);
    }, []);

    const handleDeleteRepresentative = useCallback(() => {
        if (!deleteConfirmationRepresentativeId) {
            return;
        }

        const representativeIndex = representatives.findIndex(
            (representative, index) =>
                getRepresentativeActionId(representative, index) === deleteConfirmationRepresentativeId
        );

        if (representativeIndex === -1) {
            setDeleteConfirmationRepresentativeId(null);
            return;
        }

        const nextRepresentatives = representatives.filter((_, index) => index !== representativeIndex);

        handleChange('representatives', nextRepresentatives);
        setEditingRepresentativeId(null);
        setIsEditorOpen(nextRepresentatives.length === 0);
        setDeleteConfirmationRepresentativeId(null);
    }, [deleteConfirmationRepresentativeId, getRepresentativeActionId, representatives, handleChange]);

    const filteredContactIds = useMemo(
        () => getAddedBusinessContactsIds(representatives, editingRepresentativePersonId),
        [representatives, editingRepresentativePersonId]
    );

    const editorListeners = useMemo(() => createRepresentativeEditorListeners(setDisableSaveButton), []);

    const isSaving =
        isRepresentativeSaving || contactInfoLoading || isBusinessContactsSaving || isProfileOrganizationSaving;
    const shouldShowEditor = isEditorOpen || contactInfoLoading || !hasRepresentatives;
    const shouldShowRenderer = !shouldShowEditor && hasRepresentatives;
    const editorInitialState = currentRepresentative || quickAddRepresentativeInitialData || null;

    const editorKey = editingRepresentativeId
        ? `edit-${editingRepresentativeId}`
        : `add-${quickAddRepresentativeInitialData?.personReference?.id || CONSTANTS_ID.createId}`;

    return (
        <>
            {shouldShowEditor && (
                <InlineFieldsEditor
                    key={'representative-editor-' + editorKey}
                    hideCheckbox
                    titleToken={'form.shortlistLineItem.representative'}
                    inputFields={
                        <ContactFieldInputs
                            filterById={filteredContactIds}
                            setShowPersonEditor={setShowQuickAddPerson}
                            setPersonData={setQuickAddPersonData}
                            showOrganization
                            getDefaultContactFieldsForPerson={getDefaultRepresentativeFieldsForPerson}
                            disabled={disabledFields}
                            disabledTooltip={disabledFieldsTooltip}
                        />
                    }
                    listeners={editorListeners}
                    onSave={handleSaveRepresentative}
                    onCancel={handleCloseEditor}
                    checkboxLabelToken="form.shortlistLineItem.representative.primary.contact"
                    checkboxName="isPrimary"
                    isSaving={isSaving}
                    initialState={editorInitialState}
                    saveButtonLabel={editingRepresentativeId ? 'common.update' : 'dialog.add'}
                    disableSaveButton={disableSaveButton}
                />
            )}

            {shouldShowRenderer && (
                <V gap={2}>
                    <V gap={1} sx={{ alignItems: 'flex-start' }}>
                        <FormWidget
                            component={FORM_WIDGET_COMPONENTS.sectionTitle}
                            i18nToken="form.shortlistLineItem.representative"
                            containerSx={{ textTransform: 'uppercase' }}
                        />
                        {!shouldShowEditor && (
                            <ButtonWithIcon
                                label={<I18n token="dialog.add" />}
                                Icon={Icons.AddOutlined}
                                onClick={handleAddRepresentative}
                                fontSize={SIZES.small}
                                sx={{ alignItems: 'flex-start' }}
                            />
                        )}
                    </V>
                    <V gap={1}>
                        {representativeGroups.map((group) => (
                            <V key={group.role} gap={1}>
                                <Typography variant="subtitle2" fontWeight="bold" sx={REPRESENTATIVE_ROLE_HEADING_SX}>
                                    {group.role}
                                </Typography>
                                {group.representatives.map((representative) => {
                                    return (
                                        <RepresentativeContactRenderer
                                            key={`${group.role}-${representative.representativeActionId}`}
                                            representative={representative}
                                            isActive={
                                                activeRepresentativePersonId === representative.representativeActionId
                                            }
                                            setCurrentPerson={setActiveRepresentativePersonId}
                                            onEditRepresentative={handleEditRepresentative}
                                            isDeleteConfirmationOpen={
                                                deleteConfirmationRepresentativeId ===
                                                representative.representativeActionId
                                            }
                                            handleDeleteConfirmed={handleDeleteRepresentative}
                                            setDeleteConfirmationRepresentativeId={
                                                setDeleteConfirmationRepresentativeId
                                            }
                                            entityName={representativeEntityName}
                                        />
                                    );
                                })}
                            </V>
                        ))}
                    </V>
                </V>
            )}
        </>
    );
}
